import React, { useState, useEffect } from 'react';
import usePageTitle from '../hooks/usePageTitle';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Snackbar,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import Layout from '../components/Layout';
import apartmentService from '../services/apartmentService';
import authService from '../services/authService';
import { useUnion } from '../contexts/UnionContext';

function ApartmentForm({ readOnly = false }) {
  const { t } = useTranslation();
  usePageTitle(t('nav.apartment'));
  const navigate = useNavigate();
  const { id } = useParams();
  const { activeUnion, isUnionAdmin } = useUnion();
  const isEdit = !!id && !readOnly;

  const [currentUserEmail, setCurrentUserEmail] = useState('');
  useEffect(() => {
    authService.getUserInfo().then(info => setCurrentUserEmail(info.email || '')).catch(() => {});
  }, []);

  const [formData, setFormData] = useState({
    unit_number: '',
    floor: '',
    area_sqm: '',
    ownership_share_percent: '',
    registered_residents_count: 1,
    notes: '',
    is_active: true,
  });

  const [owners, setOwners] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Owner invite by email
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);

  // Tenant invite by email
  const [tenantEmail, setTenantEmail] = useState('');
  const [addingTenant, setAddingTenant] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // True if the current user is one of this apartment's owners
  const isCurrentOwner = owners.some(o => o.owner_email === currentUserEmail);
  // Show management panels to admins and owners
  const canManage = isUnionAdmin || isCurrentOwner;

  useEffect(() => {
    if (id) loadApartment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadApartment = async () => {
    try {
      setLoading(true);
      const data = await apartmentService.getApartment(id);
      setFormData({
        unit_number: data.unit_number || '',
        floor: data.floor || '',
        area_sqm: data.area_sqm || '',
        ownership_share_percent: data.ownership_share_percent || '',
        registered_residents_count: data.registered_residents_count || 1,
        notes: data.notes || '',
        is_active: data.is_active !== undefined ? data.is_active : true,
      });
      setOwners(data.owners || []);
      setTenants((data.tenants || []).filter(t => !t.end_date));
      setPendingInvitations(data.pending_invitations || []);
      setError('');
    } catch (err) {
      setError(t('apartmentForm.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleInviteOwner = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const result = await apartmentService.inviteOwner(id, inviteEmail.trim());
      setInviteEmail('');
      await loadApartment();
      setSnackbar({
        open: true,
        message: result.status === 'invited'
          ? `Invitation sent to ${inviteEmail}`
          : `${inviteEmail} added as owner`,
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to invite owner',
        severity: 'error',
      });
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveOwner = async (ownershipId) => {
    try {
      await apartmentService.removeOwner(ownershipId);
      setOwners(owners.filter(o => o.id !== ownershipId));
    } catch {
      setError('Failed to remove owner');
    }
  };

  const handleAddTenant = async () => {
    if (!tenantEmail.trim()) return;
    setAddingTenant(true);
    try {
      const result = await apartmentService.addTenant(id, tenantEmail.trim());
      setTenantEmail('');
      await loadApartment();
      setSnackbar({
        open: true,
        message: result.status === 'invited'
          ? `Invitation sent to ${tenantEmail}`
          : `${tenantEmail} added as tenant`,
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to add tenant',
        severity: 'error',
      });
    } finally {
      setAddingTenant(false);
    }
  };

  const handleRemoveTenant = async (tenancyId) => {
    try {
      await apartmentService.removeTenant(id, tenancyId);
      setTenants(tenants.filter(t => t.id !== tenancyId));
    } catch {
      setError('Failed to remove tenant');
    }
  };

  const handleRevokeInvitation = async (invitationId) => {
    try {
      await apartmentService.revokeInvitation(id, invitationId);
      setPendingInvitations(pendingInvitations.filter(i => i.id !== invitationId));
    } catch {
      setError('Failed to revoke invitation');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      // Prepare data for submission
      const submitData = {
        ...formData,
        union: activeUnion.id,
        floor: formData.floor ? parseInt(formData.floor) : null,
        area_sqm: parseFloat(formData.area_sqm),
        ownership_share_percent: parseFloat(formData.ownership_share_percent),
        registered_residents_count: parseInt(formData.registered_residents_count),
      };

      if (isEdit) {
        await apartmentService.updateApartment(id, submitData);
      } else {
        await apartmentService.createApartment(submitData);
      }

      navigate('/apartments');
    } catch (err) {
      console.error('Failed to save apartment:', err);
      setError(err.response?.data?.detail || 'Failed to save apartment');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/apartments');
  };

  if (!activeUnion) {
    return (
      <Layout>
        <Box sx={{ py: 4 }}>
          <Alert severity="warning">
            {t('apartmentForm.selectUnion')}
          </Alert>
        </Box>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ py: 2 }}>
        <Typography variant="h4" gutterBottom>
          {readOnly ? t('apartmentForm.details') : isEdit ? t('apartmentForm.editTitle') : t('apartmentForm.addTitle')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 3, mt: 3 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('apartments.unitNumber')}
                  name="unit_number"
                  value={formData.unit_number}
                  onChange={handleChange}
                  required={!readOnly}
                  InputProps={{ readOnly }}
                  helperText={!readOnly ? t('apartmentForm.unitNumberHelper') : undefined}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('apartments.floor')}
                  name="floor"
                  type="number"
                  value={formData.floor}
                  onChange={handleChange}
                  InputProps={{ readOnly }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('apartments.areaSqm')}
                  name="area_sqm"
                  type="number"
                  inputProps={{ step: "0.01", min: "0.01" }}
                  value={formData.area_sqm}
                  onChange={handleChange}
                  required={!readOnly}
                  InputProps={{ readOnly }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('apartmentForm.ownershipShare')}
                  name="ownership_share_percent"
                  type="number"
                  inputProps={{ step: "0.001", min: "0.001", max: "100" }}
                  value={formData.ownership_share_percent}
                  onChange={handleChange}
                  required={!readOnly}
                  InputProps={{ readOnly }}
                  helperText={!readOnly ? t('apartmentForm.shareHelper') : undefined}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('apartmentForm.registeredResidents')}
                  name="registered_residents_count"
                  type="number"
                  inputProps={{ min: "0" }}
                  value={formData.registered_residents_count}
                  onChange={handleChange}
                  required={!readOnly}
                  InputProps={{ readOnly }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('apartmentForm.notes')}
                  name="notes"
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={handleChange}
                  InputProps={{ readOnly }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={handleChange}
                      name="is_active"
                      disabled={readOnly}
                    />
                  }
                  label={t('common.active')}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {readOnly ? (
                    <>
                      <Button
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={handleCancel}
                      >
                        {t('common.back')}
                      </Button>
                      {isUnionAdmin && (
                        <Button
                          variant="contained"
                          onClick={() => navigate(`/apartments/${id}/edit`)}
                        >
                          {t('common.edit')}
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <Button
                        type="submit"
                        variant="contained"
                        startIcon={<SaveIcon />}
                        disabled={saving}
                      >
                        {saving ? t('common.saving') : t('common.save')}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={handleCancel}
                        disabled={saving}
                      >
                        {t('common.cancel')}
                      </Button>
                    </>
                  )}
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>

        {/* Owners panel — shown in both view and edit mode */}
        {(isEdit || readOnly) && (
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom>{t('apartmentForm.ownersSection')}</Typography>
            <Divider sx={{ mb: 2 }} />

            {/* Current owners */}
            {owners.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('apartmentForm.noOwners')}
              </Typography>
            ) : (
              <List dense disablePadding sx={{ mb: 2 }}>
                {owners.map((o) => (
                  <ListItem key={o.id} disableGutters>
                    <ListItemText
                      primary={o.owner_full_name || o.owner_username}
                      secondary={o.owner_email}
                    />
                    {!readOnly && (
                      <ListItemSecondaryAction>
                        <IconButton edge="end" size="small" onClick={() => handleRemoveOwner(o.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                ))}
              </List>
            )}

            {/* Pending owner invitations */}
            {pendingInvitations.filter(i => i.invitation_type === 'OWNER' || !i.invitation_type).length > 0 && (
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('apartmentForm.pendingInvitations')}
                </Typography>
                <List dense disablePadding sx={{ mb: 2 }}>
                  {pendingInvitations
                    .filter(i => i.invitation_type === 'OWNER' || !i.invitation_type)
                    .map((inv) => (
                      <ListItem key={inv.id} disableGutters>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {inv.email}
                              <Chip label={t('apartmentForm.invited')} size="small" color="warning" variant="outlined" />
                            </Box>
                          }
                          secondary={t('apartmentForm.invitedBy', { name: inv.invited_by_name || '—', date: new Date(inv.expires_at).toLocaleDateString() })}
                        />
                        {canManage && (
                          <ListItemSecondaryAction>
                            <IconButton edge="end" size="small" onClick={() => handleRevokeInvitation(inv.id)} title={t('apartmentForm.revoke')}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        )}
                      </ListItem>
                    ))}
                </List>
              </>
            )}

            {/* Invite owner by email — admins only */}
            {isUnionAdmin && (
              <Box sx={{ display: 'flex', gap: 1, maxWidth: 460 }}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('apartmentForm.inviteOwner')}
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleInviteOwner()}
                  autoComplete="off"
                  disabled={inviting}
                  placeholder="owner@example.com"
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleInviteOwner}
                  disabled={inviting || !inviteEmail.trim()}
                  startIcon={inviting ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
                  sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  {inviting ? t('apartments.sending') : t('apartmentForm.sendInvite')}
                </Button>
              </Box>
            )}
          </Paper>
        )}

        {/* Tenants panel — shown to admins and owners in both view and edit mode */}
        {(isEdit || readOnly) && canManage && (
          <Paper sx={{ p: 3, mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <PeopleIcon color="action" fontSize="small" />
              <Typography variant="h6">{t('apartmentForm.tenantsSection')}</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {tenants.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('apartmentForm.noTenants')}
              </Typography>
            ) : (
              <List dense disablePadding sx={{ mb: 2 }}>
                {tenants.map((t_item) => (
                  <ListItem key={t_item.id} disableGutters>
                    <ListItemText
                      primary={t_item.tenant_full_name || t_item.tenant_username}
                      secondary={t_item.tenant_email}
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" size="small" onClick={() => handleRemoveTenant(t_item.id)} title={t('apartmentForm.removeTenant')}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}

            {/* Pending tenant invitations */}
            {pendingInvitations.filter(i => i.invitation_type === 'TENANT').length > 0 && (
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('apartmentForm.pendingTenantInvitations')}
                </Typography>
                <List dense disablePadding sx={{ mb: 2 }}>
                  {pendingInvitations
                    .filter(i => i.invitation_type === 'TENANT')
                    .map((inv) => (
                      <ListItem key={inv.id} disableGutters>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {inv.email}
                              <Chip label={t('apartmentForm.invited')} size="small" color="warning" variant="outlined" />
                            </Box>
                          }
                          secondary={t('apartmentForm.invitedBy', { name: inv.invited_by_name || '—', date: new Date(inv.expires_at).toLocaleDateString() })}
                        />
                        <ListItemSecondaryAction>
                          <IconButton edge="end" size="small" onClick={() => handleRevokeInvitation(inv.id)} title={t('apartmentForm.revoke')}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                </List>
              </>
            )}

            {/* Add tenant by email */}
            <Box sx={{ display: 'flex', gap: 1, maxWidth: 460 }}>
              <TextField
                fullWidth
                size="small"
                label={t('apartmentForm.addTenantByEmail')}
                type="email"
                value={tenantEmail}
                onChange={(e) => setTenantEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddTenant()}
                autoComplete="off"
                disabled={addingTenant}
                placeholder="tenant@example.com"
              />
              <Button
                variant="contained"
                size="small"
                onClick={handleAddTenant}
                disabled={addingTenant || !tenantEmail.trim()}
                startIcon={addingTenant ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {addingTenant ? t('apartmentForm.adding') : t('apartmentForm.addTenant')}
              </Button>
            </Box>
          </Paper>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
}

export default ApartmentForm;
